// AD-2 · Capture Resilience Store (r1).
//
// Contract: /app/memory/contracts/ad2_capture_resilience_r1.md
//
// Replaces the ephemeral `useState<Blob>` + `useState<CaptureMeta>`
// buffer in `OnTheMoveReflection.jsx` with an IndexedDB-backed
// store so an unresolved reflection survives refresh, tab close,
// browser restart, or accidental navigation.
//
// The record remains local to the learner's browser/device until
// Save, Discard or expiry. It is never transmitted as a side
// effect of being written — transmission happens only through the
// explicit Save gesture, which posts to /api/ad2/reflections and,
// on confirmed server persistence, deletes the local record.
//
// Contract-normative constants (do not tune without a new revision):
// - DATABASE_NAME     = "ndg_ad2"          (§3.1)
// - DATABASE_VERSION  = 1                   (§3.1)
// - STORE_NAME        = "captures"          (§3.1)
// - KEY_PATH          = "capture_id"        (§3.1)
// - CONTRACT_VERSION  = "ad2_capture_resilience_r1" (§3.2)
// - TTL_MS            = 7 * 24 * 60 * 60 * 1000  (§8.2 — operator-approved: 2026-02)
// - STATES enum       = closed 6-set        (§4.1)

const DATABASE_NAME = "ndg_ad2";
const DATABASE_VERSION = 1;
const STORE_NAME = "captures";
const KEY_PATH = "capture_id";
const CONTRACT_VERSION = "ad2_capture_resilience_r1";

// operator-approved: 2026-02
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// §4.1 — closed state enum. Do not add values without a new contract revision.
export const STATES = Object.freeze({
  ARMED: "armed",
  AWAITING_CHOICE: "awaiting_choice",
  SAVING: "saving",
  SAVED: "saved",
  DISCARDED: "discarded",
  EXPIRED: "expired",
});

// UUID v4 regex — used only to validate the shape of stored keys
// during §5 integrity checks; not used for generation.
const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let _dbPromise = null;
const _subscribers = new Set();

// ── Availability probe ─────────────────────────────────────────
// §9.1 — feature-detect IndexedDB. Returns false for older
// browsers, private-mode profiles that block IndexedDB, quota
// exhaustion at open time, or corporate policy blocks.
export async function isAvailable() {
  try {
    if (typeof indexedDB === "undefined" || indexedDB === null) return false;
    await _openDatabase();
    return true;
  } catch {
    return false;
  }
}

// ── Public API ─────────────────────────────────────────────────

// §15.1 — writes an `armed` record. Called at MediaRecorder start.
export async function beginCapture({ capture_id, lesson_id, mime }) {
  const record = {
    capture_id,
    lesson_id,
    blob: null,
    mime: mime || "",
    byte_length: 0,
    duration_seconds: 0,
    stop_reason: "",
    captured_at: "",
    state: STATES.ARMED,
    created_at: new Date().toISOString(),
    contract_version: CONTRACT_VERSION,
  };
  await _put(record);
  _notify();
  return record;
}

// §15.1 — transitions armed → awaiting_choice, writing the blob
// and its metadata. Called from MediaRecorder.onstop.
export async function finalizeCapture({
  capture_id,
  blob,
  byte_length,
  duration_seconds,
  stop_reason,
  captured_at,
}) {
  const existing = await _get(capture_id);
  const base = existing || {
    capture_id,
    lesson_id: "",
    contract_version: CONTRACT_VERSION,
    created_at: new Date().toISOString(),
  };
  const record = {
    ...base,
    blob,
    byte_length,
    duration_seconds,
    stop_reason,
    captured_at,
    mime: (blob && blob.type) || base.mime || "",
    state: STATES.AWAITING_CHOICE,
  };
  await _put(record);
  _notify();
  return record;
}

// §15.1 — transitions awaiting_choice → saving. Called at Save tap.
export async function beginSave(capture_id) {
  const record = await _get(capture_id);
  if (!record) return null;
  const next = { ...record, state: STATES.SAVING };
  await _put(next);
  _notify();
  return next;
}

// §15.1 — confirmed successful server persistence. §10.2 step 4.
// Transitions saving → saved and immediately deletes the record.
export async function confirmSave(capture_id) {
  await _delete(capture_id);
  _notify();
}

// §15.1 — reverts saving → awaiting_choice after any non-2xx or
// network failure. Never deletes the record. §10.2 step 5.
export async function failSave(capture_id) {
  const record = await _get(capture_id);
  if (!record) return null;
  const next = { ...record, state: STATES.AWAITING_CHOICE };
  await _put(next);
  _notify();
  return next;
}

// §15.1 — explicit Discard. Transitions to discarded and deletes.
export async function discard(capture_id) {
  await _delete(capture_id);
  _notify();
}

// §15.1 — enumerates non-terminal records, optionally filtered by
// lesson. Used at mount for §6.1 cross-lesson partitioning.
export async function listUnresolved({ lesson_id } = {}) {
  const all = await _getAll();
  return all.filter((r) => {
    if (!r || !r.state) return false;
    if (r.state === STATES.SAVED || r.state === STATES.DISCARDED) return false;
    if (lesson_id !== undefined && lesson_id !== null && lesson_id !== "") {
      return r.lesson_id === lesson_id;
    }
    return true;
  });
}

// §15.1 — light subscribe pattern for React re-renders.
export function subscribe(handler) {
  _subscribers.add(handler);
  return () => _subscribers.delete(handler);
}

// ── §5 integrity check ─────────────────────────────────────────
// Returns { ok: true } if every check passes; otherwise
// { ok: false, reason: "<check_name>" }. The caller (component)
// interprets a failed check as "quarantine to expired and route
// through explicit recovery UX (§7.3)".
export function checkIntegrity(record, currentLessonId) {
  if (!record) return { ok: false, reason: "missing" };
  // 1. Blob presence and integrity.
  if (!record.blob || typeof record.blob.size !== "number") {
    return { ok: false, reason: "blob_missing" };
  }
  if (record.blob.size !== record.byte_length || record.blob.size === 0) {
    return { ok: false, reason: "byte_length_mismatch" };
  }
  // 2. Metadata presence.
  for (const field of [
    "capture_id",
    "lesson_id",
    "duration_seconds",
    "stop_reason",
    "captured_at",
  ]) {
    if (record[field] === undefined || record[field] === null || record[field] === "") {
      return { ok: false, reason: "metadata_incomplete" };
    }
  }
  // 3. State validity.
  const valid = new Set(Object.values(STATES));
  if (!valid.has(record.state)) {
    return { ok: false, reason: "state_invalid" };
  }
  if (record.state === STATES.ARMED) {
    return { ok: false, reason: "armed_partial" };
  }
  // 4. Capture identity self-consistency.
  if (!UUID_V4_RE.test(record.capture_id)) {
    return { ok: false, reason: "capture_id_malformed" };
  }
  // 5. Lesson association integrity.
  if (!record.lesson_id || typeof record.lesson_id !== "string") {
    return { ok: false, reason: "lesson_id_malformed" };
  }
  if (currentLessonId && record.lesson_id !== currentLessonId) {
    return { ok: false, reason: "cross_lesson" };
  }
  // 6. Contract-version compatibility.
  if (record.contract_version !== CONTRACT_VERSION) {
    return { ok: false, reason: "contract_version_mismatch" };
  }
  // 7. TTL bound.
  const createdMs = Date.parse(record.created_at);
  if (Number.isNaN(createdMs)) {
    return { ok: false, reason: "created_at_malformed" };
  }
  if (Date.now() - createdMs > TTL_MS) {
    return { ok: false, reason: "ttl_exceeded" };
  }
  return { ok: true };
}

// ── §8 · TTL sweep helper ──────────────────────────────────────
// Marks any awaiting_choice/saving/armed record whose age exceeds
// TTL_MS as `expired`. NEVER deletes (§8.3). Returns count marked.
export async function sweepExpired() {
  const all = await _getAll();
  let marked = 0;
  for (const r of all) {
    if (!r || !r.created_at) continue;
    if (r.state !== STATES.AWAITING_CHOICE && r.state !== STATES.SAVING && r.state !== STATES.ARMED) {
      continue;
    }
    const createdMs = Date.parse(r.created_at);
    if (Number.isNaN(createdMs)) continue;
    if (Date.now() - createdMs > TTL_MS) {
      await _put({ ...r, state: STATES.EXPIRED });
      marked += 1;
    }
  }
  if (marked > 0) _notify();
  return marked;
}

// ── Internals ──────────────────────────────────────────────────

function _openDatabase() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined" || indexedDB === null) {
      reject(new Error("indexeddb_unavailable"));
      return;
    }
    const req = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: KEY_PATH });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("indexeddb_open_failed"));
    req.onblocked = () => reject(new Error("indexeddb_blocked"));
  });
  return _dbPromise;
}

function _put(record) {
  return _openDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(record);
        req.onsuccess = () => resolve(record);
        req.onerror = () => reject(req.error);
      }),
  );
}

function _get(capture_id) {
  return _openDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(capture_id);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
      }),
  );
}

function _getAll() {
  return _openDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      }),
  );
}

function _delete(capture_id) {
  return _openDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(capture_id);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
  );
}

function _notify() {
  for (const h of _subscribers) {
    try {
      h();
    } catch {
      // subscribers must not throw; swallow to protect the store.
    }
  }
}

// Test-only reset — never called by production code paths.
export function __resetForTests() {
  _dbPromise = null;
  _subscribers.clear();
}
